import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import theme from "./theme";
import NavBar from "./components/NavBar";
import { isAdmin, getToken } from "./auth/token";
import BACKEND_URL from "./config";

// Same auth header helper as the other admin pages.
function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Must match FUNNEL_STEPS in app/repositories/funnel.py. Labels only — the
// server decides the order and sends the steps already sequenced.
const STEP_LABELS = {
  contributor_page_viewed: "Saw /contributors",
  landing_viewed: "Saw interview landing",
  eligibility_passed: "Passed eligibility",
  overview_continued: "Read overview, continued",
  about_form_viewed: "Saw About form",
  about_form_completed: "Completed About form",
  payment_method_selected: "Chose payout method",
  start_interview_clicked: "Clicked Start",
  interview_started: "Interview created",
  preflight_mic_granted: "Allowed mic + camera",
  preflight_screen_granted: "Shared screen",
  interview_first_answer_submitted: "Answered first question",
  interview_completed: "Interview completed",
};

// Must match the payment methods the landing form offers
// (frontend/src/InterviewLandingPage.js). Venmo takes a phone number and the
// gift cards are a delivery address, so the contact field is labelled per
// method rather than called "email" everywhere.
const PAYMENT_METHOD_LABELS = {
  paypal: "PayPal",
  venmo: "Venmo",
  amazon_gift_card: "Amazon gift card",
  visa_gift_card: "Visa gift card",
};

const PAYMENT_CONTACT_LABELS = {
  paypal: "PayPal email",
  venmo: "Venmo email or phone",
  amazon_gift_card: "Gift card delivery email",
  visa_gift_card: "Gift card delivery email",
};

function paymentFields(payment) {
  const method = String(payment?.method || "").trim();
  return [
    ["Name", payment?.interviewee_name],
    ["Payout method", PAYMENT_METHOD_LABELS[method] || method],
    [PAYMENT_CONTACT_LABELS[method] || "Payment account", payment?.email],
    [
      "Payout amount",
      payment?.payout_amount != null ? `$${payment.payout_amount}` : null,
    ],
    ["Interview email", payment?.interviewee_email],
  ];
}

const SIDE_EVENT_LABELS = {
  eligibility_failed: "Failed eligibility (hadn’t used it recently)",
  reminder_added: "Added a reminder (deferred)",
  mobile_blocked: "Turned away — on a phone",
  promo_video_played: "Played the homepage promo video",
  preflight_system_check_failed: "Browser can’t record (system check failed)",
};

// A visitor's last event is often a side event — being turned away on a phone
// is the most common way to leave — so the "got as far as" lookup has to cover
// both sets or it falls back to the raw event name.
const ALL_EVENT_LABELS = { ...STEP_LABELS, ...SIDE_EVENT_LABELS };

// The segmentable columns, in the order they're shown. `field` matches the
// funnel_visitors column and the query param; `label` says where the value
// comes from, because "Campaign" alone left it ambiguous whether the value was
// something the system inferred or something you put in the ad's URL.
const DIMENSIONS = [
  {
    field: "interview_type_slug",
    label: "Interview type",
    hint: "Which study they entered — from /participate/<slug>",
  },
  { field: "utm_source", label: "Source", hint: "?utm_source= — e.g. linkedin, reddit, facebook" },
  { field: "utm_medium", label: "Medium", hint: "?utm_medium= — e.g. paid, organic" },
  { field: "utm_campaign", label: "Campaign", hint: "?utm_campaign= — e.g. str_c1" },
  { field: "utm_content", label: "Ad creative", hint: "?utm_content= — the specific ad variant" },
  { field: "utm_term", label: "Term", hint: "?utm_term= — keyword, if you tag one" },
  { field: "landing_path", label: "Landing page", hint: "The first page of the visit" },
  { field: "referrer", label: "Referrer", hint: "The site they came from, if the browser sent one" },
  { field: "li_fat_id", label: "LinkedIn click ID", hint: "Auto-appended by LinkedIn ads" },
  { field: "rdt_cid", label: "Reddit click ID", hint: "Auto-appended by Reddit ads" },
  { field: "is_mobile", label: "Device", hint: "Derived from the user agent, not the URL" },
];

const DIMENSION_LABELS = Object.fromEntries(DIMENSIONS.map((d) => [d.field, d.label]));

// Values are stored verbatim except is_mobile (a real boolean) and the
// interview type, which is stored as a slug but read as a name. `names` is the
// slug -> name map from the API; a slug with no entry (deleted study) falls
// back to the slug itself rather than rendering blank.
function displayValue(field, value, names = {}) {
  if (value === null || value === undefined || value === "") return null;
  if (field === "is_mobile") return String(value) === "true" ? "Mobile" : "Desktop";
  if (field === "interview_type_slug") return names[value] || String(value);
  return String(value);
}

function pct(value) {
  if (value === null || value === undefined) return "—";
  return `${Math.round(value * 100)}%`;
}

function FunnelTable({ data }) {
  const { steps = [], side_events: sideEvents = {} } = data || {};

  // Anchor "from top" to the widest point of the funnel, not the first step.
  // /contributors only fires for visitors who entered there, so anchoring to it
  // would put later steps above 100% for anyone who came straight to
  // /participate — the funnel has two legitimate entry points.
  const top = steps.reduce((max, s) => Math.max(max, s.visitors), 0);

  return (
    <>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Step</TableCell>
              <TableCell align="right">Visitors</TableCell>
              <TableCell align="right">From previous</TableCell>
              <TableCell align="right">From top</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {steps.map((step) => {
              // The biggest single drop is the thing worth looking at first.
              const drop =
                step.conversion_from_previous !== null &&
                step.conversion_from_previous < 0.5;
              return (
                <TableRow key={step.event_name}>
                  <TableCell>{STEP_LABELS[step.event_name] || step.event_name}</TableCell>
                  <TableCell align="right">{step.visitors}</TableCell>
                  <TableCell
                    align="right"
                    sx={drop ? { color: "error.main", fontWeight: 600 } : undefined}
                  >
                    {pct(step.conversion_from_previous)}
                  </TableCell>
                  <TableCell align="right">
                    {top ? pct(step.visitors / top) : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
        {Object.entries(sideEvents).map(([name, count]) => (
          <Chip
            key={name}
            size="small"
            color={name === "mobile_blocked" && count > 0 ? "warning" : "default"}
            label={`${SIDE_EVENT_LABELS[name] || name}: ${count}`}
          />
        ))}
      </Box>
    </>
  );
}

function VisitorTrace({ visitor, typeNames }) {
  const [open, setOpen] = useState(false);

  const lastStep = visitor.events.length
    ? visitor.events[visitor.events.length - 1].event_name
    : "—";
  const payout = visitor.events
    .map((e) => e.properties?.payout_shown)
    .find((p) => p !== undefined && p !== null);

  // Every segmentable field, with its value — the row expands to the full set
  // rather than collapsing them into one "source" column, because which
  // parameter carried the value is exactly what you're checking when you
  // verify an ad's tagging.
  const attribution = DIMENSIONS.map((d) => ({
    ...d,
    value: displayValue(d.field, visitor[d.field], typeNames),
  }));
  const tagged = attribution.filter((a) => a.value !== null && a.field !== "is_mobile");

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: open ? "unset" : undefined } }}>
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label="Show all fields">
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          {visitor.first_seen_at ? visitor.first_seen_at.slice(0, 16).replace("T", " ") : "—"}
        </TableCell>
        <TableCell>
          {displayValue("interview_type_slug", visitor.interview_type_slug, typeNames) || "—"}
        </TableCell>
        <TableCell>{displayValue("utm_source", visitor.utm_source) || <em>direct</em>}</TableCell>
        <TableCell>{visitor.utm_campaign || "—"}</TableCell>
        <TableCell>{visitor.utm_content || "—"}</TableCell>
        <TableCell align="right">{payout != null ? `$${payout}` : "—"}</TableCell>
        <TableCell>{visitor.is_mobile ? "Mobile" : "Desktop"}</TableCell>
        <TableCell>{visitor.email || <em>—</em>}</TableCell>
        <TableCell>{ALL_EVENT_LABELS[lastStep] || lastStep}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : "none" }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                All tracking fields
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                  gap: 1,
                  mb: 2,
                  mt: 0.5,
                }}
              >
                {attribution.map((a) => (
                  <Box key={a.field} sx={{ fontSize: 13 }}>
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {a.label}:{" "}
                    </Box>
                    {a.value ?? <em style={{ opacity: 0.6 }}>not set</em>}
                  </Box>
                ))}
                <Box sx={{ fontSize: 13 }}>
                  <Box component="span" sx={{ color: "text.secondary" }}>Interview: </Box>
                  {visitor.interview_id || <em style={{ opacity: 0.6 }}>none created</em>}
                </Box>
                <Box sx={{ fontSize: 13 }}>
                  <Box component="span" sx={{ color: "text.secondary" }}>Visitor ID: </Box>
                  <code style={{ fontSize: 12 }}>{visitor.visitor_id}</code>
                </Box>
              </Box>

              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                How to pay them
              </Typography>
              {visitor.payment ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                    gap: 1,
                    mb: 2,
                    mt: 0.5,
                  }}
                >
                  {paymentFields(visitor.payment).map(([label, value]) => (
                    <Box key={label} sx={{ fontSize: 13 }}>
                      <Box component="span" sx={{ color: "text.secondary" }}>
                        {label}:{" "}
                      </Box>
                      {value || <em style={{ opacity: 0.6 }}>not set</em>}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ fontSize: 13, mb: 2, mt: 0.5, color: "text.secondary" }}>
                  <em>
                    No payout details — this visitor never got as far as creating an
                    interview.
                  </em>
                </Box>
              )}

              {tagged.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No UTM parameters were captured for this visitor — they arrived without a tagged
                  link, so they count as direct traffic and can’t be attributed to an ad.
                </Alert>
              )}

              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Step-by-step
              </Typography>
              <Table size="small" sx={{ mt: 0.5 }}>
                <TableBody>
                  {visitor.events.map((e, i) => (
                    <TableRow key={`${e.event_name}-${i}`}>
                      <TableCell sx={{ width: 150, whiteSpace: "nowrap", color: "text.secondary", fontSize: 12 }}>
                        {e.occurred_at ? e.occurred_at.slice(11, 19) : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {ALL_EVENT_LABELS[e.event_name] || e.event_name}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                        {Object.entries(e.properties || {})
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(" · ")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AdminFunnel() {
  const navigate = useNavigate();
  const admin = useMemo(() => isAdmin(), []);

  const [groupBy, setGroupBy] = useState("");
  const [filters, setFilters] = useState({});
  const [days, setDays] = useState(90);
  const [summary, setSummary] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!admin) navigate("/");
  }, [admin, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json", ...authHeaders() };
      // One query param per active filter, so both endpoints see the same
      // population and the table always matches the funnel above it.
      const params = new URLSearchParams({ days: String(days) });
      Object.entries(filters).forEach(([f, v]) => v && params.set(f, v));
      if (groupBy) params.set("group_by", groupBy);

      const visitorParams = new URLSearchParams(params);
      visitorParams.delete("group_by");
      visitorParams.set("limit", "200");

      const [summaryRes, visitorsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/funnel/summary?${params}`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/funnel/visitors?${visitorParams}`, { headers }),
      ]);

      // Prefer the server's own message over the status code. A 503 here is a
      // configuration fault whose explanation names the exact fix (e.g. the
      // funnel needs STORAGE_BACKEND=postgres); "failed (500)" sent someone
      // hunting for a bug that did not exist.
      await Promise.all(
        [
          [summaryRes, "Funnel summary"],
          [visitorsRes, "Visitor traces"],
        ].map(async ([res, label]) => {
          if (res.ok) return;
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `${label} failed (${res.status})`);
        })
      );

      const summaryData = await summaryRes.json();
      const visitorsData = await visitorsRes.json();
      setSummary(summaryData);
      setVisitors(visitorsData.visitors || []);
    } catch (err) {
      console.error("Error loading funnel", err);
      setError(err.message || "Failed to load funnel data.");
    } finally {
      setLoading(false);
    }
  }, [days, groupBy, filters]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const segments = summary?.segments || {};
  const segmentNames = Object.keys(segments);
  // Only offer a dimension once the data actually contains it — a dropdown of
  // fields that were never tagged is noise, and picking one silently returns an
  // undifferentiated funnel.
  const facets = summary?.facets || {};
  const typeNames = summary?.interview_type_names || {};
  const availableDimensions = DIMENSIONS.filter((d) => (facets[d.field] || []).length > 0);
  const activeFilters = Object.entries(filters).filter(([, v]) => v);

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Participant funnel
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          First-party, from our own database — not LinkedIn’s. Ad blockers and
          tracking prevention do not affect these numbers, and unlike Campaign
          Manager this stays readable at a handful of visitors. Values come from
          the <code>utm_*</code> parameters on the link a visitor arrived
          through, captured once on their first visit and never overwritten.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
            <TextField
              select
              size="small"
              label="Time window"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              sx={{ minWidth: 150 }}
            >
              {[7, 30, 90, 365].map((d) => (
                <MenuItem key={d} value={d}>
                  Last {d} days
                </MenuItem>
              ))}
            </TextField>

            {/* Filter = narrow the population. One dropdown per dimension that
                has data, offering only values that actually occur. */}
            {availableDimensions.map((d) => (
              <TextField
                key={d.field}
                select
                size="small"
                label={d.label}
                value={filters[d.field] || ""}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, [d.field]: e.target.value }))
                }
                helperText={d.hint}
                sx={{ minWidth: 190 }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {(facets[d.field] || []).map((v) => (
                  <MenuItem key={v} value={v}>
                    {displayValue(d.field, v, typeNames) ?? v}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </Box>

          {/* Break down = split whatever survived the filters into one funnel
              per value. Deliberately separated from the filters above: the old
              single "Segment by" control conflated the two, so it was unclear
              whether choosing a dimension narrowed the data or split it. */}
          <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              select
              size="small"
              label="Break down by"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              helperText="Show one funnel per value, to compare side by side"
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">
                <em>One combined funnel</em>
              </MenuItem>
              {availableDimensions.map((d) => (
                <MenuItem key={d.field} value={d.field}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>

            {(activeFilters.length > 0 || groupBy) && (
              <Button
                size="small"
                onClick={() => {
                  setFilters({});
                  setGroupBy("");
                }}
              >
                Clear all
              </Button>
            )}
          </Box>

          {activeFilters.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Showing only:
              </Typography>
              {activeFilters.map(([field, value]) => (
                <Chip
                  key={field}
                  size="small"
                  label={`${DIMENSION_LABELS[field] || field} = ${displayValue(field, value, typeNames) ?? value}`}
                  onDelete={() => setFilters((prev) => ({ ...prev, [field]: "" }))}
                />
              ))}
            </Box>
          )}

          {availableDimensions.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No tracking parameters have been captured yet, so there is nothing to filter or
              break down by. Add <code>?utm_source=…&amp;utm_campaign=…&amp;utm_content=…</code> to
              the links in your ads and posts — those values are stored the first time a visitor
              arrives.
            </Alert>
          )}
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {segmentNames.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                No funnel events recorded in this window yet.
              </Alert>
            ) : (
              segmentNames.map((name) => (
                <Box key={name} sx={{ mb: 4 }}>
                  {groupBy && (
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {name}
                    </Typography>
                  )}
                  <FunnelTable data={segments[name]} />
                </Box>
              ))
            )}

            <Typography variant="h5" sx={{ mb: 1, mt: 4 }}>
              Individual traces
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              At these volumes this is the useful view: twelve traces tell you
              more than an aggregate of twelve ever will. Expand a row for every
              tracking field and the step-by-step timeline.
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>First seen</TableCell>
                    <TableCell>Interview type</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Ad creative</TableCell>
                    <TableCell align="right">Payout shown</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Got as far as</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visitors.map((v) => (
                    <VisitorTrace key={v.visitor_id} visitor={v} typeNames={typeNames} />
                  ))}
                  {visitors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <em>
                          {activeFilters.length
                            ? "No visitors match these filters in this window."
                            : "No visitors recorded in this window."}
                        </em>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}
